(() => {
  const canvas = document.getElementById("gradient-bg-canvas");
  if (!canvas) {
    console.warn("Background canvas element not found");
    return;
  }

  const supportsMotion = !window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const ctx = canvas.getContext("2d");

  if (!ctx) {
    console.warn("Failed to get 2D context for canvas");
    return;
  }

  const state = {
    width: window.innerWidth,
    height: window.innerHeight,
    lastFrame: 0,
    isVisible: true,
    particles: [],
    blobs: [],
    t: 0,
    frameId: null,
  };

  const config = {
    particleCount: 30,
    particleSpeed: 0.15,
    particleSize: [1.5, 3.5],
    lineDistance: 140,
    blobColors: [
      "rgba(59, 130, 246, 0.35)",
      "rgba(14, 165, 233, 0.28)",
      "rgba(16, 185, 129, 0.3)",
      "rgba(67, 56, 202, 0.25)",
    ],
  };

  const resizeCanvas = () => {
    state.width = window.innerWidth;
    state.height = window.innerHeight;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = state.width * dpr;
    canvas.height = state.height * dpr;
    canvas.style.width = `${state.width}px`;
    canvas.style.height = `${state.height}px`;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  };

  const randomBetween = (min, max) => Math.random() * (max - min) + min;

  const initParticles = () => {
    state.particles = Array.from({ length: config.particleCount }, () => ({
      x: Math.random() * state.width,
      y: Math.random() * state.height,
      vx: randomBetween(-config.particleSpeed, config.particleSpeed),
      vy: randomBetween(-config.particleSpeed, config.particleSpeed),
      size: randomBetween(config.particleSize[0], config.particleSize[1]),
      opacity: randomBetween(0.25, 0.7),
    }));
  };

  const initBlobs = () => {
    const maxDimension = Math.max(state.width, state.height);
    state.blobs = Array.from({ length: config.blobColors.length }, (_, i) => ({
      baseX: Math.random() * state.width,
      baseY: Math.random() * state.height,
      radius: randomBetween(maxDimension * 0.6, maxDimension * 1),
      color: config.blobColors[i],
      xOffset: Math.random() * Math.PI * 2,
      yOffset: Math.random() * Math.PI * 2,
    }));
  };

  const wrapParticle = (particle) => {
    if (particle.x < -20) particle.x = state.width + 20;
    if (particle.x > state.width + 20) particle.x = -20;
    if (particle.y < -20) particle.y = state.height + 20;
    if (particle.y > state.height + 20) particle.y = -20;
  };

  const drawBlobs = () => {
    state.blobs.forEach((blob, index) => {
      const x = blob.baseX + Math.sin(state.t * 0.0004 + blob.xOffset) * 120;
      const y = blob.baseY + Math.cos(state.t * 0.00035 + blob.yOffset) * 120;
      const gradient = ctx.createRadialGradient(x, y, blob.radius * 0.15, x, y, blob.radius);
      gradient.addColorStop(0, blob.color);
      gradient.addColorStop(1, "rgba(2,6,23,0)");

      ctx.globalCompositeOperation = index % 2 === 0 ? "lighter" : "screen";
      ctx.fillStyle = gradient;
      ctx.beginPath();
      ctx.arc(x, y, blob.radius, 0, Math.PI * 2);
      ctx.fill();
    });
    ctx.globalCompositeOperation = "lighter";
  };

  const drawParticles = () => {
    ctx.globalCompositeOperation = "lighter";
    state.particles.forEach((particle) => {
      particle.x += particle.vx;
      particle.y += particle.vy;
      wrapParticle(particle);

      ctx.beginPath();
      ctx.fillStyle = `rgba(148, 197, 255, ${particle.opacity})`;
      ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
      ctx.fill();
    });
  };

  const drawLines = () => {
    ctx.globalCompositeOperation = "lighter";
    for (let i = 0; i < state.particles.length; i += 1) {
      for (let j = i + 1; j < state.particles.length; j += 1) {
        const a = state.particles[i];
        const b = state.particles[j];
        const dx = a.x - b.x;
        const dy = a.y - b.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < config.lineDistance) {
          const alpha = 1 - dist / config.lineDistance;
          ctx.strokeStyle = `rgba(125, 211, 252, ${alpha * 0.45})`;
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.moveTo(a.x, a.y);
          ctx.lineTo(b.x, b.y);
          ctx.stroke();
        }
      }
    }
  };

  const draw = (timestamp) => {
    if (!supportsMotion || !state.isVisible) return;

    const elapsed = timestamp - state.lastFrame;
    if (elapsed < 1000 / 30) {
      state.frameId = requestAnimationFrame(draw);
      return;
    }
    state.lastFrame = timestamp;
    state.t += elapsed;

    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, state.width, state.height);
    ctx.setTransform(canvas.width / state.width, 0, 0, canvas.height / state.height, 0, 0);

    drawBlobs();
    drawParticles();
    drawLines();

    // Reset composite operation to default
    ctx.globalCompositeOperation = "source-over";

    state.frameId = requestAnimationFrame(draw);
  };

  const start = () => {
    if (state.frameId || !supportsMotion) return;
    state.lastFrame = performance.now();
    state.frameId = requestAnimationFrame(draw);
  };

  const stop = () => {
    if (state.frameId) {
      cancelAnimationFrame(state.frameId);
      state.frameId = null;
    }
  };

  const setVisibility = (visible) => {
    state.isVisible = visible;
    if (visible) {
      start();
    } else {
      stop();
    }
  };

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => setVisibility(entry.isIntersecting));
    },
    { root: null, threshold: 0 }
  );

  const handleResize = () => {
    resizeCanvas();
    initBlobs();
  };

  const visibilityHandler = () => setVisibility(!document.hidden);

  const init = () => {
    try {
      resizeCanvas();
      initParticles();
      initBlobs();
      if (supportsMotion) {
        observer.observe(canvas);
        start();
      } else {
        ctx.fillStyle = "rgba(30, 64, 175, 0.2)";
        ctx.fillRect(0, 0, state.width, state.height);
      }

      window.addEventListener("resize", handleResize, { passive: true });
      window.addEventListener("orientationchange", handleResize, { passive: true });
      document.addEventListener("visibilitychange", visibilityHandler);
    } catch (error) {
      console.error("Failed to initialize background canvas animation:", error);
    }
  };

  init();

  window.addEventListener("beforeunload", () => {
    stop();
    observer.disconnect();
    window.removeEventListener("resize", handleResize);
    window.removeEventListener("orientationchange", handleResize);
    document.removeEventListener("visibilitychange", visibilityHandler);
  });
})();



